import { Fragment, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './sidebar.scss';
import {
  AccountCard,
  AgxAvatar,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_Logout,
  Icon_PersonOutline,
  StaffPortalLogo,
  useDialog,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { useGetStaff } from '@urbanx/agx.staff.client';
import {
  DYNAMIC_MENU_CONFIG,
  filterMenuConfigByRoles,
  getRouteMap,
  MenuItemConfig,
  Pages,
} from 'constants/menu';
import MenuItem from './MenuItem';
import { AuthContext, isNonProductionEnvironment, StaffRole } from '@urbanx/agx.auth.client';
import { StaffAction } from 'types/staff';
import AppConfig from 'config/AppConfig';
import ArrowUpIcon from './icons/arrowup-icon';
import DevCampaignScenarioModal from 'components/DevCampaignScenario/DevCampaignScenarioModal';
import DevIcon from './icons/dev-icon';

interface Props {
  isMenuCollapsed: boolean;
  setIsMenuCollapsed: (value: boolean) => void;
}

type RouteMap = ReturnType<typeof getRouteMap>;

const getRouteConfig = (pathname: string, routeMap: RouteMap) => {
  const exactRouteConfig = routeMap[pathname];
  if (exactRouteConfig) return exactRouteConfig;

  const matchingRoute = Object.keys(routeMap)
    .filter((route) => route !== '/' && pathname.startsWith(`${route}/`))
    .sort((routeA, routeB) => routeB.length - routeA.length)[0];

  return matchingRoute ? routeMap[matchingRoute] : undefined;
};

const SideBar = (props: Props) => {
  const { isMenuCollapsed, setIsMenuCollapsed } = props;
  const { logout, userAccount } = useContext(AuthContext);
  const devCampaignScenarioDialog = useDialog(DevCampaignScenarioModal);
  const { state, pathname } = useLocation();
  const [menuConfig, setMenuConfig] = useState<MenuItemConfig[]>([]);
  const [activeLink, setActiveLink] = useState('Tasks');
  const [menuStates, setMenuStates] = useState<Record<string, boolean>>({
    urbanx: false,
    reporting: false,
  });
  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;
  const isTabletPortrait = screenSize === Breakpoints.Tablet_Portrait;
  const inactiveIconColor = 'var(--fg-navigation)';
  const activeIconColor = 'var(--fg-navigation-active)';
  const currentStaffId = userAccount?.agent ?? '';
  const { data: currentStaff } = useGetStaff(currentStaffId, currentStaffId !== '');

  const staffName = [currentStaff?.firstName, currentStaff?.lastName].filter(Boolean).join(' ');
  const accountName = staffName || userAccount?.firstName || 'Staff';
  const accountRole = currentStaff?.title || 'Staff';
  const accountActions = [
    {
      action: 'editProfile' as const,
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar type="icon-light" size="xs" icon={<Icon_PersonOutline />} />
          <AgxTypography id="staff-edit-profile-label" type="body_ui" size="small" color="inherit">
            Edit Profile
          </AgxTypography>
        </AgxSpacer>
      ),
      to: '/staff',
    },
    {
      action: 'logout' as const,
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar type="icon-light" size="xs" icon={<Icon_Logout />} />
          <AgxTypography id="staff-logout-label" type="body_ui" size="small" color="inherit">
            Logout
          </AgxTypography>
        </AgxSpacer>
      ),
      onClick: logout,
    },
  ];

  const showDevScenarioMenu =
    isNonProductionEnvironment(AppConfig.environment) &&
    [StaffRole.Executive, StaffRole.SuperAdmin].some((role) => userAccount?.roles?.includes(role));

  const isActiveInSubmenu = (menuId: string) => {
    const menuItem = menuConfig.find((item) => item.id === menuId);
    if (!menuItem?.submenuItems) return false;
    return menuItem.submenuItems.some((subItem) => subItem.title === activeLink);
  };

  // Accordion behaviour: opening one dropdown closes the others so the menu
  // never grows beyond the viewport.
  const toggleState = (key: string) => {
    setMenuStates((prev) => {
      const next: Record<string, boolean> = {};
      for (const stateKey of Object.keys(prev)) next[stateKey] = false;
      next[key] = !prev[key];
      return next;
    });
  };

  const updateActiveLink = (link: string) => {
    setActiveLink(link);
    if (isMobile || isTabletPortrait) {
      setIsMenuCollapsed(true);
    }
  };

  const openDevCampaignScenarioModal = () => {
    devCampaignScenarioDialog.open();
    updateActiveLink('Create Test Campaign');
  };

  const renderDevMenuShort = () => {
    if (!showDevScenarioMenu) return null;

    return (
      <li key="dev-menu-short">
        <div className="menu-item" onClick={openDevCampaignScenarioModal}>
          <DevIcon
            color={activeLink === 'Create Test Campaign' ? activeIconColor : inactiveIconColor}
            opacity={1}
            padding="0"
          />
        </div>
      </li>
    );
  };

  const renderDevMenu = () => {
    if (!showDevScenarioMenu) return null;

    const isOpen = menuStates.dev;
    const isActive = activeLink === 'Create Test Campaign';

    return (
      <li key="dev-menu" className={`has-submenu ${isOpen ? 'open' : ''}`} data-submenu-count={1}>
        <div
          className={`submenuContainer ${isActive ? 'active' : ''}`}
          onClick={() => toggleState('dev')}
          id="div-Dev"
          data-testid="div-Dev"
        >
          <div className="menu-item">
            <DevIcon
              color={isActive ? activeIconColor : inactiveIconColor}
              opacity={1}
              padding="0"
            />
            <span className={`menu-item-text ${isActive ? 'submenu-active-text' : ''}`}>Dev</span>
          </div>
          <ArrowUpIcon
            extraClasses="ic"
            color={isActive ? activeIconColor : inactiveIconColor}
            opacity={1}
            padding="0"
          />
        </div>
        <div className="submenuWrapper">
          <ul className="submenu">
            <li id="li-CreateTestCampaign" data-testid="li-CreateTestCampaign">
              <button
                type="button"
                className="dev-submenu-action"
                onClick={openDevCampaignScenarioModal}
              >
                <div className={`submenu-item ${isActive ? 'submenu-item-active' : ''}`}>
                  <span className={`menu-item-text ${isActive ? 'submenu-active-text' : ''}`}>
                    Create Test Campaign
                  </span>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </li>
    );
  };

  useEffect(() => {
    if (userAccount) {
      setMenuConfig(filterMenuConfigByRoles(DYNAMIC_MENU_CONFIG, userAccount.roles as StaffRole[]));
    }
  }, [userAccount]);

  useEffect(() => {
    if (pathname === Pages.CREATE_STAFF) {
      setActiveLink('Staff Members');
      setMenuStates({ urbanx: true, reporting: false });
      return;
    }

    if (pathname.startsWith(`${Pages.EDIT_STAFF}/`)) {
      const userId = decodeURIComponent(pathname.replace(`${Pages.EDIT_STAFF}/`, ''));
      const isOwnEditPage =
        state?.action === StaffAction.SELF_EDIT || (userId !== '' && userId === userAccount?.agent);

      setActiveLink(isOwnEditPage ? 'Settings' : 'Staff Members');
      setMenuStates({ urbanx: true, reporting: false });
      return;
    }

    const routeConfig = getRouteConfig(pathname, getRouteMap());
    if (routeConfig) {
      if (pathname === '/staff' && state?.action === StaffAction.SELF_EDIT) {
        setActiveLink('Settings');
      } else if (pathname === '/staff' && state?.action === 'view') {
        setActiveLink('Staff Members');
      } else {
        setActiveLink(routeConfig.activeLink);
      }
      if (routeConfig.menuStates) {
        setMenuStates(routeConfig.menuStates);
      }
    }
  }, [pathname, state, userAccount?.agent]);

  return (
    <div
      className={`sidebar ${isMenuCollapsed ? 'collapsed' : 'sidebar-expanded'}`}
      onMouseEnter={() => {
        setIsMenuCollapsed(false);
      }}
      onMouseLeave={() => {
        setIsMenuCollapsed(true);
      }}
    >
      <nav>
        <ul className="menuShort">
          <li>
            <div className="menu-item">
              <StaffPortalLogo color="var(--fg-navigation-logo)" />
            </div>
          </li>
          {DYNAMIC_MENU_CONFIG.filter((item) => !item.isHidden).map((item) => {
            const isActive = item.title === activeLink || isActiveInSubmenu(item.id);
            return (
              <Fragment key={item.id}>
                <li>
                  <div className="menu-item">
                    {item.icon && (
                      <item.icon
                        color={isActive ? activeIconColor : inactiveIconColor}
                        opacity={1}
                        padding="0"
                      />
                    )}
                  </div>
                </li>
                {item.id === 'urbanx' && renderDevMenuShort()}
              </Fragment>
            );
          })}
        </ul>
      </nav>
      <nav>
        <ul className={`menu ${isMenuCollapsed ? 'collapsed' : ''}`}>
          <li>
            <div className="autoPilotIcon autopilotLogo">
              <StaffPortalLogo color="var(--fg-navigation-logo)" hover={!isMenuCollapsed} />
            </div>
          </li>
          {DYNAMIC_MENU_CONFIG.filter((item) => !item.isHidden).map((item) => {
            const isActive = item.title === activeLink;
            const isActiveInSub = isActiveInSubmenu(item.id);

            return (
              <Fragment key={item.id}>
                <MenuItem
                  item={item}
                  isActive={isActive}
                  isMenuCollapsed={isMenuCollapsed}
                  menuStates={menuStates}
                  onToggleState={toggleState}
                  onUpdateActiveLink={updateActiveLink}
                  isActiveInSubmenu={isActiveInSub}
                  activeLink={activeLink}
                  activeIconColor={activeIconColor}
                  inactiveIconColor={inactiveIconColor}
                />
                {item.id === 'urbanx' && renderDevMenu()}
              </Fragment>
            );
          })}
        </ul>
      </nav>
      <div className="sidebarAccountCard">
        <AccountCard
          id="sidebar-account-card"
          name={accountName}
          role={accountRole}
          photoUrl={currentStaff?.photo?.publicPath}
          collapsed={isMenuCollapsed}
          actions={accountActions}
          renderLink={({ to, children, className, role, onClick, 'data-testid': dataTestId }) => (
            <Link
              to={to}
              state={{ action: StaffAction.SELF_EDIT }}
              className={className}
              role={role}
              data-testid={dataTestId}
              onClick={onClick}
            >
              {children}
            </Link>
          )}
          dataTestId="sidebar-account-card"
        />
      </div>
    </div>
  );
};

export default SideBar;
